import { useState, useEffect, useCallback, useRef } from "react";
import { getBalanceService } from "../services/tokenService";
import { fetchSessionService, toSession } from "../services/authenticationService";

/**
 * Sesión del usuario y estado de acceso al contenido en vivo.
 *
 * `access` es la respuesta del backend (`can_watch_live`, `denial_reason`,
 * `days_remaining`...). El criterio del muro de pago vive en el servidor; aquí
 * solo se cachea para que la UI pueda explicarlo sin volver a decidirlo.
 */
export const useAuth = () => {
    const [user, setUser] = useState(() => {
        const storedUser = localStorage.getItem("user");
        return storedUser ? JSON.parse(storedUser) : null;
    });

    const [balance, setBalance] = useState(null);
    const [access, setAccess] = useState(null);
    const [accessLoading, setAccessLoading] = useState(false);
    const [accessError, setAccessError] = useState(null);
    // Hasta que el servidor confirme la sesión no se sabe qué puede hacer el
    // usuario: sin esto, al recargar dentro de una ruta protegida la interfaz
    // decide con la copia de localStorage y expulsa a quien sí tenía acceso.
    const [sessionReady, setSessionReady] = useState(() => !localStorage.getItem("user"));
    const mounted = useRef(true);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    /**
     * Roles y permisos vigentes según el servidor.
     *
     * La sesión se guarda entera en localStorage, permisos incluidos, y allí
     * se queda: cambiarle el rol a alguien, o cambiar el catálogo de permisos,
     * no tenía ningún efecto sobre las pestañas ya abiertas, que seguían
     * enseñando pantallas que la API rechazaba. Se revalida al arrancar.
     */
    useEffect(() => {
        const stored = localStorage.getItem("user");
        if (!stored) return;

        const { token } = JSON.parse(stored);
        if (!token) return;

        let active = true;

        fetchSessionService(token)
            .then((result) => {
                if (!active) return;
                const session = { ...toSession({ ...result, token }), token };
                setUser(session);
                localStorage.setItem("user", JSON.stringify(session));
            })
            .catch((error) => {
                if (!active) return;
                // 401: el token ya no vale y hay que volver a entrar. Cualquier
                // otro fallo es de red y no debe cerrar una sesión buena.
                if (error?.status === 401) {
                    setUser(null);
                    localStorage.removeItem("user");
                }
            })
            .finally(() => {
                if (active) setSessionReady(true);
            });

        return () => {
            active = false;
        };
    }, []);

    const refreshBalance = useCallback(async () => {
        if (!user || !user.token) return;
        setAccessLoading(true);
        setAccessError(null);
        try {
            const data = await getBalanceService(user.token);
            if (!mounted.current) return;
            setBalance(data.tokens ?? 0);
            setAccess(data);
        } catch (error) {
            // Sin este fallback el saldo quedaba en null y las vistas
            // protegidas se congelaban en el spinner.
            if (mounted.current) {
                setBalance(0);
                setAccess({ can_watch_live: false, denial_reason: 'unavailable' });
                setAccessError(error?.message || "No fue posible consultar tu acceso");
            }
        } finally {
            if (mounted.current) setAccessLoading(false);
        }
    }, [user]);

    useEffect(() => {
        if (user) {
            refreshBalance();
        } else {
            setBalance(null);
            setAccess(null);
            setAccessError(null);
        }
    }, [user, refreshBalance]);

    const login = (userData) => {
        setUser(userData);
        localStorage.setItem("user", JSON.stringify(userData));
    };

    const updateBalance = (newBalance) => {
        setBalance(newBalance);
    };

    /** Aplica el estado de acceso devuelto por otra operación (p. ej. recarga). */
    const updateAccess = (nextAccess) => {
        if (!nextAccess) return;
        setAccess(nextAccess);
        if (typeof nextAccess.tokens === 'number') setBalance(nextAccess.tokens);
    };

    const logout = () => {
        setUser(null);
        setBalance(null);
        setAccess(null);
        setAccessError(null);
        localStorage.removeItem("user");
    };

    return {
        user,
        sessionReady,
        balance,
        access,
        accessLoading,
        accessError,
        login,
        logout,
        refreshBalance,
        updateBalance,
        updateAccess,
    };
};
